<template>
  <v-container>
    <v-card class="pa-5">
      <!-- Centered Title -->
      <v-card-title class="justify-center">
        <span class="text-h5" style="color: #02803f">
          Job Performance Summary
        </span>
      </v-card-title>

      <v-row dense>
        <v-col cols="12">
          <v-alert type="info" dense outlined>
            <strong>Total Jobs:</strong>
            {{ totalJobs }}
            <strong>Active:</strong>
            {{ activeJobs.length }}
            <strong>Inactive:</strong>
            {{ totalJobs - activeJobs.length }}
          </v-alert>
        </v-col>
      </v-row>

      <v-row class="px-4" align="center" justify="space-between" dense>
        <!-- Info Message -->
        <v-alert
          type="info"
          dense
          text
          outlined
          class="mb-2"
          border="left"
          color="primary"
          style="font-size: 13px"
        >
          Data is synced from MYOB for the selected date range below. These
          <span style="font-weight: bold">Start Date</span> &
          <span style="font-weight: bold">End Date</span>
          fields are for reference only.
        </v-alert>
      </v-row>
      <!-- Search & Filter -->
      <v-row class="px-4" align="center" justify="space-between" dense>
        <!-- Search Input -->
        <v-col cols="12" sm="4" md="4" lg="4">
          <v-text-field
            v-model="search"
            label="Search by Job Number or Name"
            dense
            outlined
            clearable
            append-icon="mdi-magnify"
          />
        </v-col>
        <!-- Center: Readonly Start & End Date Fields -->
        <v-col cols="12" sm="4" md="4" lg="4">
          <div class="d-flex">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              readonly
              dense
              outlined
              class="mr-2"
            />
            <v-text-field
              v-model="endDate"
              label="End Date"
              readonly
              dense
              outlined
            />
          </div>
        </v-col>
        <!-- Tooltip + Sorting -->
        <v-col
          cols="12"
          sm="4"
          md="4"
          lg="4"
          class="d-flex justify-end align-center"
        >
          <!-- Tooltip -->
          <v-tooltip top open-delay="200">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="primary"
                class="mr-3"
                v-bind="attrs"
                v-on="on"
                style="cursor: pointer"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <div
              style="
                background: white;
                color: black;
                padding: 10px;
                border-radius: 6px;
                min-width: 220px;
              "
            >
              <strong>Conditional Formatting</strong><br /><br />
              <v-row dense no-gutters>
                <v-col cols="12" class="d-flex align-center mb-1">
                  <div
                    style="
                      width: 12px;
                      height: 12px;
                      background-color: #ffc7ce;
                      margin-right: 8px;
                      border: 1px solid #ccc;
                    "
                  ></div>
                  Profit Margin &lt; 12% → Red Row
                </v-col>
                <v-col cols="12" class="d-flex align-center mb-1">
                  <div
                    style="
                      width: 12px;
                      height: 12px;
                      background-color: #83cceb;
                      margin-right: 8px;
                      border: 1px solid #ccc;
                    "
                  ></div>
                  12% – 25% → Blue Row
                </v-col>
                <v-col cols="12" class="d-flex align-center mb-1">
                  <div
                    style="
                      width: 12px;
                      height: 12px;
                      background-color: #66ff66;
                      margin-right: 8px;
                      border: 1px solid #ccc;
                    "
                  ></div>
                  &gt; 25% → Green Row
                </v-col>
                <v-col cols="12" class="d-flex align-center">
                  <div
                    style="
                      width: 12px;
                      height: 12px;
                      background-color: #f0f0f0;
                      margin-right: 8px;
                      border: 1px solid #ccc;
                    "
                  ></div>
                  = 0 → Grey Row
                </v-col>
              </v-row>
            </div>
          </v-tooltip>

          <!-- Sorting Menu -->
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-subheader>Sort By</v-subheader>
              <v-list-item
                v-for="metric in sortableMetrics"
                :key="metric.value"
                @click="toggleSort(metric.value)"
              >
                <v-list-item-title>
                  <span
                    :class="
                      sortBy === metric.value
                        ? 'font-weight-bold'
                        : 'text--disabled'
                    "
                  >
                    {{ metric.text }}
                    <span v-if="sortBy === metric.value">
                      — {{ sortDesc ? "High to Low" : "Low to High" }}
                    </span>
                  </span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <!-- Loader -->
      <v-card-text v-if="loading" class="text-center">
        <v-progress-circular indeterminate color="primary" size="48" />
        <div class="mt-2 font-italic">
          Please wait — loading job performance data.
        </div>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        v-else
        :headers="headers"
        :items="filteredJobs"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        dense
        class="mt-4 job-performance-table"
      >
        <template v-slot:item="{ item }">
          <tr :style="rowStyle(item)">
            <td>{{ item.jobNumber }}</td>
            <td>{{ item.jobName }}</td>
            <td>{{ formatCurrency(item.income) }}</td>
            <td>{{ formatCurrency(item.cost) }}</td>
            <td>{{ formatCurrency(item.expense) }}</td>
            <td>{{ formatCurrency(item.netProfit) }}</td>
            <td>{{ item.profitMargin.toFixed(2) }}%</td>
            <td>
              <v-checkbox
                :input-value="item.status === 'Active'"
                :disabled="true"
                hide-details
                dense
                class="ma-0 pa-0"
              />
            </td>
          </tr>
        </template>

        <template v-slot:header.profitMargin="{ header }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" style="cursor: help">
                {{ header.text }}
                <v-icon color="white" small class="ml-1"
                  >mdi-help-circle</v-icon
                >
              </span>
            </template>
            <span>Profit Margin (%) = (Net Profit ÷ Income) × 100</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
        <v-snackbar v-model="snackbar" timeout="4000" color="success" top right>
  {{ snackbarMsg }}
</v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "JobPerformanceSummary",
  data() {
    return {
      loading: true,
      search: "",
      sortBy: "netProfit",
      totalJobs: 0,
      activeJobs: [],
      sortDesc: true,
      startDate: "2019-01-01",
      endDate: "2030-12-31",
      jobReport: [],
      snackbar: false,
      snackbarMsg: "",
      headers: [
        { text: "Job Number", value: "jobNumber" },
        { text: "Job Name", value: "jobName" },
        { text: "Income", value: "income", sortable: true },
        { text: "Cost", value: "cost", sortable: true },
        { text: "Expenses", value: "expense", sortable: true },
        { text: "Net Profit", value: "netProfit", sortable: true },
        { text: "Profit Margin", value: "profitMargin", sortable: true },
        { text: "Active", value: "status", sortable: true, align: "center" },
      ],
      sortableMetrics: [
        { text: "Job Number", value: "jobNumber" },
        { text: "Income", value: "income" },
        { text: "Cost", value: "cost" },
        { text: "Expenses", value: "expense" },
        { text: "Net Profit", value: "netProfit" },
        { text: "Profit Margin", value: "profitMargin" },
        { text: "Active Status", value: "status" },
      ],
    };
  },
  computed: {
    filteredJobs() {
      const keyword = this.search.trim().toLowerCase();
      if (!keyword) return this.sortedJobs;

      return this.sortedJobs.filter((job) => {
        const number = job.jobNumber?.toLowerCase() || "";
        const name = job.jobName?.toLowerCase() || "";
        return number.includes(keyword) || name.includes(keyword);
      });
    },
    sortedJobs() {
      const sorted = [...this.jobReport];
      if (!this.sortBy) return sorted;

      sorted.sort((a, b) => {
        const key = this.sortBy;
        let result = 0;
        if (key === "jobNumber") {
          const aAlpha = a[key].replace(/\d+/g, "") || "";
          const bAlpha = b[key].replace(/\d+/g, "") || "";
          const aNum = parseInt(a[key].replace(/\D+/g, ""), 10) || 0;
          const bNum = parseInt(b[key].replace(/\D+/g, ""), 10) || 0;

          if (aAlpha === bAlpha) {
            return this.sortDesc ? bNum - aNum : aNum - bNum;
          } else {
            return this.sortDesc
              ? bAlpha.localeCompare(aAlpha)
              : aAlpha.localeCompare(bAlpha);
          }
        } else if (key === "status") {
          // Active first if ascending, Inactive first if descending
          const aStatus = a.status === "Active" ? 1 : 0;
          const bStatus = b.status === "Active" ? 1 : 0;
          result = this.sortDesc ? aStatus - bStatus : bStatus - aStatus;
        } else {
          result = this.sortDesc ? b[key] - a[key] : a[key] - b[key];
        }
         if (result === 0) {
      const aNum = parseInt(a.jobNumber?.replace(/\D+/g, ""), 10) || 0;
      const bNum = parseInt(b.jobNumber?.replace(/\D+/g, ""), 10) || 0;
      result = aNum - bNum;
    }

    return result;
      });

      return sorted;
    },
  },
  mounted() {
    this.fetchJobs();
    this.fetchReport();
  },
  methods: {
    async fetchReport() {
      this.loading = true;
      try {
        const res = await axios.get(`/api/jobs/profit-loss-report`);
        const listRes = await axios.get(`/api/jobs/list`);
        const jobList = listRes.data;

        const jobStatusMap = {};
        jobList.forEach((job) => {
          jobStatusMap[job.Number] = job.IsActive;
        });

        this.jobReport = (res.data.data || []).map((job) => ({
          ...job,
          status: jobStatusMap[job.jobNumber] ? "Active" : "Inactive",
        }));
        this.snackbarMsg = `Report generated successfully!`;
        this.snackbar = true;
      } catch (err) {
        console.error("Error fetching report:", err);
        this.snackbarMsg = "❌ Failed to load report. Please try again.";
        this.snackbar = true;
        this.jobReport = [];
      } finally {
        this.loading = false;
      }
    },
    // Fetch job list for the dropdown
    async fetchJobs() {
      try {
        this.Loading = true;
        const response = await axios.get(`/api/jobs/list`);
        const jobs = response.data.filter((job) => job.IsActive);
        this.activeJobs = jobs.map((job) => ({
          value: job.Number,
          text: `${job.Number} - ${job.Name}`,
        }));
        this.totalJobs = response.data.length;
        this.Loading = false;
      } catch (error) {
        console.error("Error fetching job list:", error);
      }
    },
    toggleSort(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = field;
        this.sortDesc = true;
      }
    },
    rowStyle(item) {
      const margin = item.profitMargin;
      if (margin === 0) return { backgroundColor: "#e0e0e0" };
      if (margin < 12) return { backgroundColor: "#f5a3a3" };
      if (margin <= 25) return { backgroundColor: "#a3d3f5" };
      if (margin > 25) return { backgroundColor: "#a5f2a1" };
      return {};
    },
    formatCurrency(val) {
      return `$${Number(val || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
};
</script>

<style scoped>
.v-data-table-header th {
  white-space: nowrap;
}
.text-h5 {
  font-size: 1.25rem;
  font-weight: 500;
}
.job-performance-table >>> thead {
  background-color: #02803f !important;
  color: white !important;
}
.job-performance-table >>> th {
  color: white !important;
  font-weight: 600;
  text-transform: capitalize;
    text-align: center !important;
  vertical-align: middle !important;
}
.job-performance-table >>> td {
  text-align: center !important;
  vertical-align: middle !important;
}
.job-performance-table >>> td:nth-child(8) {
  display: flex !important;
  justify-content: center !important;
}
.job-performance-table >>> .v-data-table-header th .v-icon {
  color: white !important;
}
</style>
